import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Gamepad2, 
  Clock, 
  Activity,
  Server,
  Wifi,
  ArrowLeft,
  Crown,
  Zap,
  BarChart3,
  ExternalLink,
  Maximize2,
  Download,
  Upload,
  Car,
  MapPin,
  FileArchive,
  Trash2,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '../config/api';

const SimracingPage = ({ user: userProp }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [serverConfig, setServerConfig] = useState(null);
  const [liveTimingWindow, setLiveTimingWindow] = useState(null);
  const [files, setFiles] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', description: '', file_type: 'car' });
  const [priceMonthly, setPriceMonthly] = useState(null); // dynamic price from /payments/prices
  const fileInputRef = useRef(null);
  // Local copy of user — the page is now a public route, so we fetch it
  // from /auth/me on mount when no prop is provided.
  const [user, setUser] = useState(userProp || null);

  // Use authoritative is_admin flag from the backend user model. The previous
  // hardcoded ADMIN_EMAILS list drifted easily; this is the same field shown
  // in the header badge.
  const userIsAdmin = !!user?.is_admin;

  // Only an active simracing subscription grants access. PRO users NOT
  // auto-included — simracing is a separate premium. Expiration enforced
  // server-side via auto_downgrade_if_expired; here we also double-check
  // that subscription_expires (if present) has not passed yet.
  const simracingSubscriptionValid = (() => {
    if (user?.plan !== 'simracing') return false;
    if (!user?.subscription_expires) return true; // no expiry → valid
    try {
      return new Date(user.subscription_expires) > new Date();
    } catch {
      return false;
    }
  })();
  const hasSimracingAccess = simracingSubscriptionValid || userIsAdmin;

  const handleBack = () => {
    if (user) {
      navigate('/app');
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    // Always load the public price for the landing card so non-subscribers
    // see the configured value.
    loadPublicPrice();
    // If we don't have a user yet, try to fetch it silently. Failure is
    // expected for non-logged-in visitors and is treated as "guest".
    if (!user) {
      axios
        .get(`${API}/auth/me`, { withCredentials: true })
        .then((res) => setUser(res.data))
        .catch(() => {});
    }
    if (hasSimracingAccess) {
      loadServerConfig();
      loadFiles();
    }
  }, [hasSimracingAccess, user]);

  const loadPublicPrice = async () => {
    try {
      const response = await axios.get(`${API}/payments/prices`);
      const sim = response?.data?.prices?.simracing_monthly;
      if (typeof sim === 'number') setPriceMonthly(sim);
    } catch {
      // Silent: keep priceMonthly null and the UI will show "Consultar"
    }
  };

  const loadServerConfig = async () => {
    try {
      const response = await axios.get(`${API}/simracing/server-info`, {
        withCredentials: true
      });
      setServerConfig(response.data);
    } catch (error) {
      // Empty — first-time visit, no config yet.
    }
  };

  const loadFiles = async () => {
    try {
      const response = await axios.get(`${API}/simracing/files`, {
        withCredentials: true
      });
      setFiles(response.data.files || []);
      setIsAdmin(response.data.is_admin || false);
    } catch (error) {
      // Empty — list endpoint not yet seeded.
    }
  };

  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!uploadForm.name.trim()) {
      toast.error('Ingresa un nombre para el archivo');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Use chunked upload for files > 50MB to bypass proxy body-size limits.
    const CHUNK_THRESHOLD = 50 * 1024 * 1024;
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB per chunk

    try {
      if (file.size > CHUNK_THRESHOLD) {
        // 1) Init session
        const initRes = await axios.post(
          `${API}/simracing/files/upload-chunk/init`, null,
          { withCredentials: true }
        );
        const uploadId = initRes.data.upload_id;

        // 2) Send chunks sequentially
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const blob = file.slice(start, end);
          const fd = new FormData();
          fd.append('chunk', blob, `chunk_${i}`);
          fd.append('chunk_index', String(i));
          await axios.post(
            `${API}/simracing/files/upload-chunk/${uploadId}`, fd,
            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
          setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
        }

        // 3) Finalize
        const finalForm = new FormData();
        finalForm.append('name', uploadForm.name);
        finalForm.append('description', uploadForm.description);
        finalForm.append('file_type', uploadForm.file_type);
        finalForm.append('original_filename', file.name);
        await axios.post(
          `${API}/simracing/files/upload-chunk/${uploadId}/finalize`, finalForm,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success('Archivo subido correctamente');
      } else {
        // Standard upload for small files
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', uploadForm.name);
        formData.append('description', uploadForm.description);
        formData.append('file_type', uploadForm.file_type);

        await axios.post(`${API}/simracing/files/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (evt) => {
            if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        });
        toast.success('Archivo subido correctamente');
      }
      setUploadForm({ name: '', description: '', file_type: 'car' });
      loadFiles();
    } catch (error) {
      console.error('Upload error', error);
      toast.error(error.response?.data?.detail || 'Error al subir el archivo');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const response = await axios.get(`${API}/simracing/files/${fileId}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Descargando ${fileName}`);
      loadFiles(); // Refresh to update download count
    } catch (error) {
      toast.error('Error al descargar el archivo');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!confirm('¿Estás seguro de eliminar este archivo?')) return;
    
    try {
      await axios.delete(`${API}/simracing/files/${fileId}`, {
        withCredentials: true
      });
      toast.success('Archivo eliminado');
      loadFiles();
    } catch (error) {
      toast.error('Error al eliminar el archivo');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
  };

  // Build the proxied URL (HTTPS) so the live timing works inside the
  // HTTPS app without "mixed content" blocks.
  const liveTimingProxyUrl = serverConfig?.live_timing_url
    ? `${API}/simracing/live-timing-proxy`
    : null;
  // Direct URL of the live timing server. We use this when opening in a NEW
  // tab / window — the browser allows HTTP destinations from a top-level
  // navigation (no mixed content issue), and the proxy occasionally rewrites
  // assets/WebSocket URLs which breaks the standalone view.
  const liveTimingDirectUrl = serverConfig?.live_timing_url || null;
  // Detect if the configured live_timing_url is HTTP (insecure) so we can warn
  // the user that the embedded view goes through our HTTPS proxy.
  const liveTimingIsHttp = (serverConfig?.live_timing_url || '').toLowerCase().startsWith('http://');

  const openLiveTiming = () => {
    if (!liveTimingDirectUrl) return;
    const width = 1400;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const newWindow = window.open(
      liveTimingDirectUrl,
      'LiveTiming',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    setLiveTimingWindow(newWindow);
    toast.success('Live Timing abierto en ventana separada');
  };

  const openLiveTimingFullscreen = () => {
    if (!liveTimingDirectUrl) return;
    window.open(liveTimingDirectUrl, '_blank');
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        plan_id: 'simracing_monthly',
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Error al iniciar el pago');
    } finally {
      setLoading(false);
    }
  };

  // If user doesn't have simracing access, show upgrade prompt
  if (!hasSimracingAccess) {
    return (
      <div className="min-h-screen bg-black">
        {/* Header */}
        <header className="bg-neutral-900 border-b border-red-900/30">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" onClick={handleBack}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
                <div className="flex items-center gap-3">
                  <Gamepad2 className="w-8 h-8 text-red-500" />
                  <div>
                    <h1 className="text-xl font-bold text-white">Simracing</h1>
                    <p className="text-xs text-gray-400">Entrenamientos Virtuales</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Upgrade Prompt */}
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto">
            <Card className="relative bg-gradient-to-br from-neutral-900 to-neutral-800 border-red-900/30 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-orange-600/10 pointer-events-none"></div>
              <CardContent className="relative p-8 md:p-12">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-600/20 mb-6">
                    <Gamepad2 className="w-10 h-10 text-red-500" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-4">
                    DATAPRO Simracing
                  </h2>
                  <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                    Accede a nuestro servidor exclusivo de Assetto Corsa y lleva tu entrenamiento al siguiente nivel
                  </p>
                </div>

                {/* Features */}
                <div className="grid md:grid-cols-2 gap-6 mb-10">
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-black/30">
                    <Server className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white">Servidor Dedicado</h3>
                      <p className="text-sm text-gray-400">Assetto Corsa con circuitos argentinos y configuraciones reales</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-black/30">
                    <Activity className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white">Live Timing</h3>
                      <p className="text-sm text-gray-400">Seguí los tiempos en vivo de todos los pilotos</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-black/30">
                    <BarChart3 className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white">Analytics Completo</h3>
                      <p className="text-sm text-gray-400">Todo lo incluido en PRO para análisis de carreras reales</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-black/30">
                    <Gamepad2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-white">Comunidad Exclusiva</h3>
                      <p className="text-sm text-gray-400">Entrena con otros pilotos y compará tiempos</p>
                    </div>
                  </div>
                </div>

                {/* Price and CTA */}
                <div className="text-center">
                  <div className="mb-6">
                    {priceMonthly != null ? (
                      <>
                        <span
                          className="text-5xl font-bold text-white"
                          data-testid="simracing-price"
                        >
                          {new Intl.NumberFormat('es-AR', {
                            style: 'currency',
                            currency: 'ARS',
                            maximumFractionDigits: 0,
                          }).format(priceMonthly)}
                        </span>
                        <span className="text-gray-400 ml-2">/mes</span>
                      </>
                    ) : (
                      <span className="text-2xl font-semibold text-gray-300" data-testid="simracing-price-loading">
                        Cargando precio…
                      </span>
                    )}
                  </div>
                  <Button 
                    onClick={handleSubscribe}
                    disabled={loading}
                    className="bg-red-600 hover:bg-red-700 text-white px-12 py-6 text-lg font-semibold"
                    data-testid="subscribe-simracing-btn"
                  >
                    {loading ? (
                      <>Procesando...</>
                    ) : (
                      <>
                        <Zap className="w-5 h-5 mr-2" />
                        Suscribirse a Simracing
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-gray-500 mt-4">
                    Pago seguro con MercadoPago
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // User has simracing access - show dashboard
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-neutral-900 border-b border-red-900/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div className="flex items-center gap-3">
                <Gamepad2 className="w-8 h-8 text-red-500" />
                <div>
                  <h1 className="text-xl font-bold text-white">Simracing</h1>
                  <p className="text-xs text-gray-400">Entrenamientos Virtuales</p>
                </div>
              </div>
            </div>
            <Badge className="bg-gradient-to-r from-red-600 to-orange-600 text-white">
              <Crown className="w-3 h-3 mr-1" />
              SIMRACING
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Server Card */}
        <Card className="bg-gradient-to-br from-neutral-900 to-neutral-800 border-red-900/30 mb-8">
          <CardContent className="p-8">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Server Icon */}
              <div className="w-24 h-24 bg-gradient-to-br from-red-600/30 to-orange-600/30 border-2 border-red-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Server className="w-12 h-12 text-red-500" />
              </div>
              
              {/* Server Info */}
              <div className="flex-1 text-center lg:text-left">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {serverConfig?.server_name || 'Servidor de Entrenamientos'}
                </h2>
                {serverConfig?.track_name && (
                  <p className="text-gray-400 mb-4">
                    Circuito: <span className="text-white">{serverConfig.track_name}</span>
                  </p>
                )}
                
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  {serverConfig?.server_link && (
                    <a 
                      href={serverConfig.server_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Button 
                        size="lg" 
                        className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
                      >
                        <Gamepad2 className="w-5 h-5 mr-2" />
                        Ingresar a la sesión
                      </Button>
                    </a>
                  )}
                </div>
              </div>
              
              {/* Status */}
              <div className="flex flex-col items-center gap-2">
                <Badge 
                  variant="outline" 
                  className={serverConfig?.server_link 
                    ? "border-green-500 text-green-500 px-4 py-2" 
                    : "border-yellow-500 text-yellow-500 px-4 py-2"
                  }
                >
                  <Wifi className="w-4 h-4 mr-2" />
                  {serverConfig?.server_link ? 'Online' : 'Offline'}
                </Badge>
                {serverConfig?.max_players && (
                  <span className="text-sm text-gray-500">
                    Capacidad: {serverConfig.max_players} pilotos
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live Timing Card (embedded iframe) */}
        {serverConfig?.live_timing_url && (
          <Card className="bg-gradient-to-br from-green-900/20 to-neutral-900 border-green-900/30 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-600/30 to-emerald-600/30 border-2 border-green-500 rounded-2xl flex items-center justify-center relative">
                    <Activity className="w-6 h-6 text-green-500" />
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold text-white">Live Timing</h2>
                      <Badge className="bg-green-600 text-white animate-pulse">EN VIVO</Badge>
                    </div>
                    <p className="text-xs text-gray-500">
                      Tiempos en tiempo real · vueltas, sectores, posiciones
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openLiveTimingFullscreen}
                    className="border-green-500 text-green-400 hover:bg-green-950"
                    data-testid="live-timing-fullscreen-btn"
                  >
                    <Maximize2 className="w-4 h-4 mr-1.5" />
                    Pantalla Completa
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={openLiveTiming}
                    className="text-gray-400 hover:text-white"
                    data-testid="live-timing-newtab-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" />
                    Abrir en pestaña
                  </Button>
                </div>
              </div>

              {/* Embedded iframe (via HTTPS proxy to bypass mixed-content) */}
              {/* Responsive height: short on mobile, taller on tablet/desktop.
                  We use fixed heights instead of an aspect-ratio because the
                  Assetto Server Manager renders a wide table that gets squished
                  in portrait orientations. */}
              <div
                className="relative w-full bg-black rounded-lg overflow-hidden border border-green-900/30 h-[480px] sm:h-[560px] md:h-[640px] lg:h-[720px] xl:h-[760px]"
                data-testid="live-timing-embed-wrapper"
              >
                <iframe
                  src={liveTimingProxyUrl}
                  title="Live Timing Simracing"
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  allow="fullscreen; clipboard-read; clipboard-write"
                  allowFullScreen
                  referrerPolicy="no-referrer"
                  scrolling="auto"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  data-testid="live-timing-iframe"
                />
              </div>

              <p className="text-[11px] text-gray-500 mt-2">
                {liveTimingIsHttp ? (
                  <>
                    El servidor de live timing usa HTTP, así que se carga a través
                    de nuestro proxy HTTPS para evitar bloqueos del navegador. Si
                    los tiempos no se actualizan en tiempo real (WebSocket
                    bloqueado), abrilo en{' '}
                    <span className="text-green-400">"Pantalla Completa"</span>{' '}
                    o <span className="text-green-400">"Abrir en pestaña"</span>.
                  </>
                ) : (
                  <>
                    Si el live timing no carga embebido (algunos servidores
                    bloquean iframes), usá{' '}
                    <span className="text-green-400">"Pantalla Completa"</span>{' '}
                    o <span className="text-green-400">"Abrir en pestaña"</span>.
                  </>
                )}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Downloadable Files Section */}
        <Card className="bg-gradient-to-br from-blue-900/20 to-neutral-900 border-blue-900/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-3">
              <FileArchive className="w-6 h-6 text-blue-500" />
              Archivos Descargables
              <Badge variant="outline" className="border-blue-500 text-blue-500 ml-2">
                MODS
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Admin Upload Section */}
            {isAdmin && (
              <div className="mb-6 p-4 bg-black/30 rounded-lg border border-blue-900/30">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4 text-blue-400" />
                  Subir Nuevo Archivo (Admin)
                </h3>
                <div className="grid md:grid-cols-4 gap-3">
                  <Input
                    placeholder="Nombre del archivo"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                  />
                  <Input
                    placeholder="Descripción (opcional)"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                    className="bg-neutral-800 border-neutral-700 text-white"
                  />
                  <Select 
                    value={uploadForm.file_type} 
                    onValueChange={(v) => setUploadForm({ ...uploadForm, file_type: v })}
                  >
                    <SelectTrigger className="bg-neutral-800 border-neutral-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral-800 border-neutral-700">
                      <SelectItem value="car" className="text-white">🚗 Auto (Mod)</SelectItem>
                      <SelectItem value="track" className="text-white">🏁 Pista (Track)</SelectItem>
                    </SelectContent>
                  </Select>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".zip,.rar,.7z"
                    />
                    <Button 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || !uploadForm.name.trim()}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {uploading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo {uploadProgress > 0 && `${uploadProgress}%`}</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> Subir Archivo</>
                      )}
                    </Button>
                    {uploading && uploadProgress > 0 && (
                      <div className="w-full bg-neutral-800 rounded-full h-2 mt-2 overflow-hidden">
                        <div
                          className="bg-blue-500 h-full transition-all duration-200"
                          style={{ width: `${uploadProgress}%` }}
                          data-testid="upload-progress-bar"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Files List */}
            {files.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileArchive className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No hay archivos disponibles aún</p>
                {isAdmin && <p className="text-sm mt-1">Subí el primer archivo usando el formulario de arriba</p>}
              </div>
            ) : (
              <div className="space-y-3">
                {/* Cars Section */}
                {files.filter(f => f.file_type === 'car').length > 0 && (
                  <div>
                    <h4 className="text-gray-400 text-sm font-semibold mb-2 flex items-center gap-2">
                      <Car className="w-4 h-4" /> AUTOS (MODS)
                    </h4>
                    <div className="space-y-2">
                      {files.filter(f => f.file_type === 'car').map((file) => (
                        <div 
                          key={file.id} 
                          className="flex items-center justify-between p-4 bg-neutral-900 rounded-lg border border-neutral-800 hover:border-blue-900/50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-600/20 rounded-lg flex items-center justify-center">
                              <Car className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{file.name}</p>
                              {file.description && <p className="text-gray-500 text-sm">{file.description}</p>}
                              <p className="text-gray-600 text-xs">
                                {formatFileSize(file.file_size)} • {file.download_count} descargas
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleDownload(file.id, file.original_filename)}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              <Download className="w-4 h-4 mr-1" /> Descargar
                            </Button>
                            {isAdmin && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDeleteFile(file.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-950"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tracks Section */}
                {files.filter(f => f.file_type === 'track').length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-gray-400 text-sm font-semibold mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> PISTAS (TRACKS)
                    </h4>
                    <div className="space-y-2">
                      {files.filter(f => f.file_type === 'track').map((file) => (
                        <div 
                          key={file.id} 
                          className="flex items-center justify-between p-4 bg-neutral-900 rounded-lg border border-neutral-800 hover:border-green-900/50 transition-colors"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-green-600/20 rounded-lg flex items-center justify-center">
                              <MapPin className="w-5 h-5 text-green-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{file.name}</p>
                              {file.description && <p className="text-gray-500 text-sm">{file.description}</p>}
                              <p className="text-gray-600 text-xs">
                                {formatFileSize(file.file_size)} • {file.download_count} descargas
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleDownload(file.id, file.original_filename)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Download className="w-4 h-4 mr-1" /> Descargar
                            </Button>
                            {isAdmin && (
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDeleteFile(file.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-950"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SimracingPage;
